import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Bot, CheckCircle2, CreditCard, ExternalLink, Layers3, Loader2, Play, ShieldCheck, Sparkles, Workflow, XCircle } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { Link, useParams } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

type ProductKind = "web" | "messaging";

type ProductUnit = {
  id: string;
  name: string;
  description: string;
  risk: "low" | "medium" | "high";
  disclaimer?: string;
  serviceIds?: string[];
  requiresConsent?: boolean;
  requiresHumanApproval?: boolean;
  requiresHumanReview?: boolean;
  requiresPayment?: boolean;
};

type Product = {
  id: string;
  name: string;
  priority: string;
  segment: string;
  positioning: string;
  serviceIds?: string[];
  modules?: ProductUnit[];
  tools?: ProductUnit[];
  workflow?: string[];
  useCases?: string[];
  guardrails: string[];
  disclaimers?: string[];
  pricing: Array<{ name: string; priceUsd: string; limits: Record<string, number | string> }>;
};

type CheckoutResult = {
  success?: boolean;
  provider?: string;
  checkoutStatus?: "requires_payment" | "authorized" | "pending_manual" | string;
  checkoutUrl?: string;
  runToken?: string;
  paymentToken?: string;
  workspace?: { id?: string; plan?: string; type?: string };
  quote?: { displayPrice?: string; amountCents?: number; currency?: string; tierName?: string };
};

type AccountContext = {
  user?: { id: string; email: string; phone?: string };
  memberships: Array<{ productKind: "web" | "suite"; workspaceId: string; role: string; status: string }>;
  subscriptions: Array<{
    id: string;
    productKind: "web" | "suite";
    productId: string;
    workspaceId: string;
    plan: string;
    tierName: string;
    provider: string;
    status: "pending" | "active" | "past_due" | "paused" | "canceled" | "expired";
    checkoutUrl?: string;
    currentPeriodEnd?: string;
  }>;
};

type SubscriptionCheckoutResult = {
  success?: boolean;
  provider?: string;
  checkoutStatus?: string;
  checkoutUrl?: string;
  sessionToken?: string;
  subscription?: AccountContext["subscriptions"][number];
  workspace?: { id?: string; plan?: string; type?: string };
  quote?: { displayPrice?: string; amountCents?: number; currency?: string; tierName?: string };
};

export function AiProductView() {
  const { productKind = "web", productId = "" } = useParams<{ productKind: ProductKind; productId: string }>();
  const kind: ProductKind = productKind === "messaging" ? "messaging" : "web";
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [payload, setPayload] = useState("");
  const [structuredPayload, setStructuredPayload] = useState<Record<string, string>>({});
  const [checkoutRunToken, setCheckoutRunToken] = useState("");
  const [checkoutSuiteToken, setCheckoutSuiteToken] = useState("");
  const [checkoutSuiteWorkspaceId, setCheckoutSuiteWorkspaceId] = useState("");
  const [accountToken, setAccountToken] = useState(() => (typeof window === "undefined" ? "" : window.localStorage.getItem("ai-suite-account-token") || ""));
  const [accountEmail, setAccountEmail] = useState("");
  const [accountNotice, setAccountNotice] = useState("");
  const [subscribedWorkspaceId, setSubscribedWorkspaceId] = useState("");
  const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const webProductToken = searchParams?.get("web_product_token") || null;
  const suitePaymentToken = searchParams?.get("suite_payment_token") || null;
  const suiteWorkspaceId = searchParams?.get("workspace_id") || null;
  const paymentStatus = searchParams?.get("payment") || null;
  const paymentProvider = searchParams?.get("provider") || null;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const token = query.get("account_token") || hash.get("account_token");
    if (!token) return;
    window.localStorage.setItem("ai-suite-account-token", token);
    setAccountToken(token);
    query.delete("account_token");
    hash.delete("account_token");
    const search = query.toString();
    const fragment = hash.toString();
    const cleanUrl = `${window.location.pathname}${search ? `?${search}` : ""}${fragment ? `#${fragment}` : ""}`;
    window.history.replaceState({}, "", cleanUrl);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (accountToken) window.localStorage.setItem("ai-suite-account-token", accountToken);
    else window.localStorage.removeItem("ai-suite-account-token");
  }, [accountToken]);

  const { data: product, isLoading, isError, error, refetch } = useQuery<Product>({
    queryKey: ["ai-product", kind, productId],
    queryFn: async () => {
      const prefix = kind === "web" ? "web-products" : "products";
      const res = await fetch(`${API_BASE}/${prefix}/products/${productId}`);
      if (!res.ok) throw new Error("Product not found");
      const body = await res.json();
      return body.product;
    },
  });

  const accountQuery = useQuery<AccountContext>({
    queryKey: ["ai-account", accountToken],
    enabled: Boolean(accountToken),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/account/me`, { headers: { Authorization: `Bearer ${accountToken}` } });
      if (!res.ok) throw new Error("Account session expired or invalid");
      return res.json();
    },
  });

  const units = useMemo(() => product ? (kind === "web" ? product.modules || [] : product.tools || []) : [], [kind, product]);
  const selectedUnit = units.find((unit) => unit.id === selectedUnitId) || units[0];
  const productFields = useMemo(() => selectedUnit ? fieldsForUnit(kind, selectedUnit.id, product?.id || "") : [], [kind, product?.id, selectedUnit]);
  const parsedPayload = useMemo(() => buildOperationalPayload(kind, product?.id || "", selectedUnit?.id || "", payload, structuredPayload), [kind, product?.id, selectedUnit?.id, payload, structuredPayload]);
  const hasOperationalInput = payload.trim().length > 0 || Object.values(structuredPayload).some((value) => value.trim().length > 0);
  const selectedUnitNeedsPayment = kind === "web" && selectedUnit?.requiresPayment !== false;
  const primaryPaidTier = product?.pricing.find((tier) => tier.priceUsd !== "0") || product?.pricing[0];
  const accountProductKind = kind === "web" ? "web" : "suite";
  const activeSubscription = useMemo(() => {
    if (!product) return undefined;
    return accountQuery.data?.subscriptions.find((subscription) =>
      subscription.productKind === accountProductKind
      && subscription.productId === product.id
      && subscription.status === "active"
    );
  }, [accountProductKind, accountQuery.data?.subscriptions, product]);
  const activeWorkspaceId = subscribedWorkspaceId || activeSubscription?.workspaceId || checkoutSuiteWorkspaceId || suiteWorkspaceId || "";

  const billingQuery = useQuery({
    queryKey: ["ai-account-billing", accountProductKind, activeWorkspaceId, accountToken],
    enabled: Boolean(accountToken && activeWorkspaceId),
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/account/workspaces/${accountProductKind}/${activeWorkspaceId}/billing`, {
        headers: { Authorization: `Bearer ${accountToken}` },
      });
      if (!res.ok) throw new Error("No se pudo cargar billing del workspace");
      return res.json() as Promise<{ active: boolean; subscription?: AccountContext["subscriptions"][number] }>;
    },
  });

  const loginMutation = useMutation({
    mutationFn: async () => {
      if (!accountEmail.trim()) throw new Error("Ingresa tu email para enviar el enlace de acceso.");
      const returnPath = typeof window !== "undefined"
        ? window.location.pathname.replace(/^\/oracle/, "")
        : `/ai-suite/products/${kind}/${productId}`;
      const res = await fetch(`${API_BASE}/account/auth/request-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: accountEmail.trim(), returnPath }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "No se pudo enviar el enlace de acceso");
      if (body.token) {
        const verifyRes = await fetch(`${API_BASE}/account/auth/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: body.token }),
        });
        const verified = await verifyRes.json().catch(() => ({}));
        if (!verifyRes.ok) throw new Error(verified.error || "No se pudo verificar el enlace de acceso");
        return { ...body, ...verified };
      }
      return body;
    },
    onSuccess: (body) => {
      if (body.sessionToken) {
        setAccountToken(body.sessionToken);
        setAccountNotice("Sesión iniciada. Ya puedes suscribirte o abrir tu workspace.");
        return;
      }
      setAccountNotice("Enlace enviado. Revisa tu correo para iniciar sesión y volver a este producto.");
    },
  });

  const subscriptionMutation = useMutation<SubscriptionCheckoutResult>({
    mutationFn: async () => {
      if (!product) throw new Error("Producto no cargado");
      if (!accountToken && !accountEmail.trim()) throw new Error("Ingresa tu email para crear cuenta y suscripción.");
      const returnPath = typeof window !== "undefined"
        ? window.location.pathname.replace(/^\/oracle/, "")
        : `/ai-suite/products/${kind}/${product.id}`;
      const res = await fetch(`${API_BASE}/account/subscriptions/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accountToken ? { Authorization: `Bearer ${accountToken}` } : {}),
        },
        body: JSON.stringify({
          productKind: accountProductKind,
          productId: product.id,
          plan: primaryPaidTier?.name,
          ownerEmail: accountEmail.trim() || accountQuery.data?.user?.email,
          workspaceName: `${product.name} workspace`,
          returnPath,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || body.message || "No se pudo crear la suscripción");
      return body;
    },
    onSuccess: (checkout) => {
      if (checkout.sessionToken) setAccountToken(checkout.sessionToken);
      if (checkout.workspace?.id) setSubscribedWorkspaceId(checkout.workspace.id);
      if (checkout.checkoutUrl) {
        window.location.href = checkout.checkoutUrl;
        return;
      }
      setAccountNotice(`Suscripción ${checkout.subscription?.status || checkout.checkoutStatus || "creada"} para este workspace.`);
      void accountQuery.refetch();
      void billingQuery.refetch();
    },
  });

  const demoMutation = useMutation({
    mutationFn: async () => {
      if (!product || !selectedUnit) throw new Error("Selecciona una herramienta");
      if (!hasOperationalInput) throw new Error("Agrega un input de prueba o completa al menos un campo operativo antes de ejecutar.");
      const prefix = kind === "web" ? "web-products" : "products";
      const unitPath = kind === "web" ? "modules" : "tools";
      const operationalPayload = { text: payload, request: payload, description: payload, source: "product-page", ...parsedPayload };

      if (kind === "web") {
        if (activeSubscription && activeWorkspaceId && accountToken) {
          const executeRes = await fetch(`${API_BASE}/${prefix}/products/${product.id}/${unitPath}/${selectedUnit.id}/execute`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accountToken}`,
              "idempotency-key": `${product.id}-${selectedUnit.id}-subscription-run-${Date.now()}`,
            },
            body: JSON.stringify({ workspaceId: activeWorkspaceId, payload: operationalPayload }),
          });
          const executeBody = await executeRes.json().catch(() => ({}));
          if (!executeRes.ok) throw new Error(executeBody.message || executeBody.error || "Product execution failed");
          return executeBody;
        }

        let runToken = webProductToken || checkoutRunToken || "";
        if (selectedUnit.requiresPayment !== false && !runToken) {
          const returnPath = typeof window !== "undefined"
            ? window.location.pathname.replace(/^\/oracle/, "")
            : `/ai-suite/products/${kind}/${product.id}`;
          const checkoutRes = await fetch(`${API_BASE}/${prefix}/products/${product.id}/checkout`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "idempotency-key": `${product.id}-${selectedUnit.id}-checkout-${Date.now()}`,
              ...(accountToken ? { Authorization: `Bearer ${accountToken}` } : {}),
            },
            body: JSON.stringify({ moduleId: selectedUnit.id, payload: operationalPayload, returnPath }),
          });
          const checkout = await checkoutRes.json().catch(() => ({}));
          if (!checkoutRes.ok) throw new Error(checkout.message || checkout.error || "Checkout failed");
          if (checkout.checkoutUrl) {
            window.location.href = checkout.checkoutUrl;
            return { success: false, result: "Redirigiendo a pago..." };
          }
          runToken = checkout.runToken || "";
        }

        const executeRes = await fetch(`${API_BASE}/${prefix}/products/${product.id}/${unitPath}/${selectedUnit.id}/execute`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "idempotency-key": `${product.id}-${selectedUnit.id}-run-${Date.now()}` },
          body: JSON.stringify({ payload: operationalPayload, runToken }),
        });
        const executeBody = await executeRes.json().catch(() => ({}));
        if (!executeRes.ok) throw new Error(executeBody.message || executeBody.error || "Product execution failed");
        return executeBody;
      }

      if (activeSubscription && activeWorkspaceId && accountToken) {
        const res = await fetch(`${API_BASE}/${prefix}/products/${product.id}/${unitPath}/${selectedUnit.id}/execute`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accountToken}`,
            "idempotency-key": `${product.id}-${selectedUnit.id}-subscription-run-${Date.now()}`,
          },
          body: JSON.stringify({ workspaceId: activeWorkspaceId, payload: operationalPayload }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || body.error || "Product execution failed");
        return body;
      }

      let paymentToken = suitePaymentToken || checkoutSuiteToken || checkoutMutation.data?.paymentToken || "";
      let workspaceId = suiteWorkspaceId || checkoutSuiteWorkspaceId || checkoutMutation.data?.workspace?.id || "";
      if (!paymentToken || !workspaceId) {
        const returnPath = typeof window !== "undefined"
          ? window.location.pathname.replace(/^\/oracle/, "")
          : `/ai-suite/products/${kind}/${product.id}`;
        const checkoutRes = await fetch(`${API_BASE}/${prefix}/products/${product.id}/checkout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "idempotency-key": `${product.id}-${selectedUnit.id}-checkout-${Date.now()}`,
            ...(accountToken ? { Authorization: `Bearer ${accountToken}` } : {}),
          },
          body: JSON.stringify({ plan: primaryPaidTier?.name, workspaceName: `${product.name} workspace`, returnPath }),
        });
        const checkout = await checkoutRes.json().catch(() => ({}));
        if (!checkoutRes.ok) throw new Error(checkout.message || checkout.error || "Checkout failed");
        if (checkout.checkoutUrl) {
          window.location.href = checkout.checkoutUrl;
          return { success: false, result: "Redirigiendo a pago..." };
        }
        paymentToken = checkout.paymentToken || "";
        workspaceId = checkout.workspace?.id || "";
        if (paymentToken) setCheckoutSuiteToken(paymentToken);
        if (workspaceId) setCheckoutSuiteWorkspaceId(workspaceId);
      }
      if (!paymentToken || !workspaceId) throw new Error("Checkout no devolvió paymentToken/workspace para ejecutar el producto.");

      const res = await fetch(`${API_BASE}/${prefix}/products/${product.id}/${unitPath}/${selectedUnit.id}/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "idempotency-key": `${product.id}-${selectedUnit.id}-${Date.now()}` },
        body: JSON.stringify({ payload: operationalPayload, paymentToken, workspaceId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || "Product execution failed");
      return body;
    },
  });

  const checkoutMutation = useMutation<CheckoutResult>({
    mutationFn: async () => {
      if (!product) throw new Error("Producto no cargado");
      const returnPath = typeof window !== "undefined"
        ? `${window.location.pathname.replace(/^\/oracle/, "")}${window.location.search || ""}`
        : `/ai-suite/products/${kind}/${product.id}`;
      const prefix = kind === "web" ? "web-products" : "products";
      const body = kind === "web"
        ? { moduleId: selectedUnit?.id, payload: { text: payload, request: payload, source: "product-page-checkout", ...parsedPayload }, returnPath }
        : { plan: primaryPaidTier?.name, workspaceName: `${product.name} workspace`, returnPath };
      const res = await fetch(`${API_BASE}/${prefix}/products/${product.id}/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "idempotency-key": `${product.id}-${selectedUnit?.id || "product"}-checkout-${Date.now()}`,
          ...(accountToken ? { Authorization: `Bearer ${accountToken}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const checkout = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(checkout.message || checkout.error || "Checkout failed");
      if (checkout.checkoutUrl) {
        window.location.href = checkout.checkoutUrl;
        return { ...checkout, checkoutStatus: checkout.checkoutStatus || "requires_payment" };
      }
      return checkout;
    },
    onSuccess: (checkout) => {
      if (checkout.runToken) setCheckoutRunToken(checkout.runToken);
      if (checkout.paymentToken) setCheckoutSuiteToken(checkout.paymentToken);
      if (checkout.workspace?.id) setCheckoutSuiteWorkspaceId(checkout.workspace.id);
    },
  });

  if (isLoading) return <div className="p-8 text-slate-300">Cargando producto...</div>;
  if (isError) {
    return (
      <div className="mx-auto max-w-3xl rounded-[1.5rem] border border-rose-200/25 bg-rose-500/10 p-8 text-rose-50">
        <h1 className="text-2xl font-black">No se pudo cargar el producto</h1>
        <p className="mt-3 text-sm leading-6 text-rose-100/85">{error.message || "La API no respondió correctamente."}</p>
        <Button className="mt-6 rounded-full bg-white px-6 text-slate-950 hover:bg-rose-100" onClick={() => refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }
  if (!product) return <div className="p-8 text-slate-300">Producto no encontrado.</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-8 pb-16">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" asChild className="rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
          <Link to="/ai-suite">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a la suite
          </Link>
        </Button>
        <Button variant="outline" asChild className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10">
          <Link to={`/ai-suite/landings/${kind}/${product.id}`}>
            Ver landing de pauta
            <ExternalLink className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>

      <section className="rounded-[2rem] border border-white/10 bg-white/[0.055] p-6 shadow-2xl sm:p-8">
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge>{kind === "web" ? "Producto Web" : "WhatsApp / Telegram"}</Badge>
              <Badge>{product.priority}</Badge>
              <Badge>{`${units.length} herramientas`}</Badge>
            </div>
            <h1 className="text-4xl font-black tracking-tight sm:text-5xl">{product.name}</h1>
            <p className="max-w-3xl text-lg leading-8 text-slate-300">{product.positioning}</p>
            <p className="max-w-3xl text-sm leading-7 text-slate-400">{product.segment}</p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-black/20 p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-200/10 text-cyan-100">
                {kind === "web" ? <Layers3 className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
              </div>
              <div>
                <p className="text-sm font-bold">Producto vendible</p>
                <p className="text-sm text-slate-400">Demo pública + rutas protegidas para producción</p>
              </div>
            </div>
            <div className="mt-5 space-y-2">
              {product.pricing.slice(0, 3).map((tier) => (
                <InfoRow key={tier.name} label={tier.name} value={tier.priceUsd === "0" ? "Free" : `USD ${tier.priceUsd}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <DisclaimerNotice disclaimers={product.disclaimers || []} selectedUnit={selectedUnit} />

      {(paymentStatus || webProductToken || suitePaymentToken) && (
        <div className="rounded-[1.5rem] border border-emerald-200/25 bg-emerald-500/10 p-5 text-emerald-50">
          <div className="flex items-center gap-2 text-sm font-black">
            <CheckCircle2 className="h-4 w-4" />
            Pago/checkout recibido
          </div>
          <p className="mt-2 text-sm leading-6 text-emerald-100/85">
            {kind === "web"
              ? "Ya puedes ejecutar este módulo con el runToken devuelto por el checkout. No cambies el input si el pago estaba atado al hash del payload."
              : "El checkout del producto conversacional volvió a la suite. Usa el workspace/token para activar operación autenticada."}
            {paymentProvider ? ` Proveedor: ${paymentProvider}.` : ""}
          </p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        <Card className="h-fit rounded-[1.5rem] border-white/10 bg-white/[0.055]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-cyan-200" />
              Usar producto
            </CardTitle>
            <CardDescription>Selecciona una herramienta del producto y ejecuta una demo operativa.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3">
              {units.map((unit) => (
                <button
                  key={unit.id}
                  type="button"
                  onClick={() => setSelectedUnitId(unit.id)}
                  className={`rounded-2xl border p-4 text-left transition ${
                    selectedUnit?.id === unit.id ? "border-cyan-200/40 bg-cyan-200/10" : "border-white/10 bg-black/20 hover:bg-white/[0.06]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-black text-white">{unit.name}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-400">{unit.description}</p>
                    </div>
                    <RiskBadge risk={unit.risk} />
                  </div>
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-200">Input de prueba</label>
              <Textarea
                value={payload}
                onChange={(event) => setPayload(event.target.value)}
                placeholder={kind === "web" ? "Describe el archivo, documento, campaña o tarea que quieres procesar..." : "Escribe el mensaje de WhatsApp/audio transcrito/situación que quieres manejar..."}
                className="min-h-[160px] border-white/10 bg-black/20"
              />
            </div>

            {productFields.length ? (
              <div className="grid gap-3 rounded-2xl border border-white/10 bg-black/20 p-4">
                <div>
                  <p className="text-sm font-black text-white">Campos operativos</p>
                  <p className="mt-1 text-xs leading-5 text-slate-400">Estos campos evitan demos ambiguas y alimentan el backend con datos accionables.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {productFields.map((field) => (
                    <label key={field.name} className="space-y-1.5">
                      <span className="text-xs font-bold text-slate-300">{field.label}</span>
                      <Input
                        value={structuredPayload[field.name] || ""}
                        onChange={(event) => setStructuredPayload((current) => ({ ...current, [field.name]: event.target.value }))}
                        placeholder={field.placeholder}
                        className="border-white/10 bg-slate-950/70"
                      />
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-cyan-200/15 bg-cyan-300/10 p-4 text-xs leading-5 text-cyan-50">
              Payload que se enviará al backend: <code className="text-cyan-100">{Object.keys(parsedPayload).filter((key) => parsedPayload[key] !== "").slice(0, 8).join(", ") || "solo texto"}</code>
            </div>

            <PaymentPanel
              kind={kind}
              product={product}
              selectedUnit={selectedUnit}
              accountEmail={accountEmail}
              onAccountEmailChange={setAccountEmail}
              account={accountQuery.data}
              accountError={accountQuery.error instanceof Error ? accountQuery.error.message : ""}
              accountNotice={accountNotice}
              activeSubscription={activeSubscription}
              billingActive={Boolean(billingQuery.data?.active)}
              activeWorkspaceId={activeWorkspaceId}
              loginError={loginMutation.error}
              isLoggingIn={loginMutation.isPending}
              onLogin={() => loginMutation.mutate()}
              subscriptionData={subscriptionMutation.data}
              subscriptionError={subscriptionMutation.error}
              isSubscribing={subscriptionMutation.isPending}
              onSubscribe={() => subscriptionMutation.mutate()}
              hasWebToken={Boolean(webProductToken || checkoutRunToken)}
              suitePaymentToken={suitePaymentToken || checkoutSuiteToken}
              suiteWorkspaceId={suiteWorkspaceId || checkoutSuiteWorkspaceId}
              checkoutData={checkoutMutation.data}
              checkoutError={checkoutMutation.error}
              isCheckingOut={checkoutMutation.isPending}
              onCheckout={() => checkoutMutation.mutate()}
            />

            <Button
              className="h-12 w-full rounded-2xl bg-cyan-200 text-base font-black text-slate-950 hover:bg-white"
              onClick={() => demoMutation.mutate()}
              disabled={demoMutation.isPending || !selectedUnit}
            >
              {demoMutation.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5 fill-current" />}
              {activeSubscription ? "Ejecutar con mi suscripción" : kind === "web" && selectedUnitNeedsPayment && !webProductToken && !checkoutRunToken ? "Checkout y ejecutar producto" : "Ejecutar demo del producto"}
            </Button>

            {selectedUnit?.serviceIds?.length ? (
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Workers reales</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedUnit.serviceIds.map((serviceId) => (
                    <Button key={serviceId} asChild variant="outline" className="h-9 rounded-full border-white/10 bg-white/5 text-xs">
                      <Link to={`/ai-suite/${serviceId}`}>
                        Abrir {serviceId}
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </Link>
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-[1.5rem] border-white/10 bg-white/[0.045]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Workflow className="h-5 w-5 text-emerald-200" />
                Flujo de venta y operación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(product.workflow || defaultWorkflow(kind)).map((step, index) => (
                <div key={step} className="grid grid-cols-[2.25rem_1fr] gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-200 font-black text-slate-950">{index + 1}</div>
                  <p className="pt-1.5 text-sm leading-6 text-slate-200">{step}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="min-h-[360px] rounded-[1.5rem] border-white/10 bg-white/[0.045]">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Resultado
                {demoMutation.isSuccess && <CheckCircle2 className="h-5 w-5 text-emerald-300" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {demoMutation.isPending ? (
                <div className="flex min-h-[260px] items-center justify-center">
                  <Loader2 className="h-12 w-12 animate-spin text-cyan-200" />
                </div>
              ) : demoMutation.isError ? (
                <div className="rounded-2xl border border-red-300/20 bg-red-500/10 p-4 text-sm text-red-100">
                  <div className="flex items-center gap-2 font-black">
                    <XCircle className="h-4 w-4" />
                    No se pudo completar el flujo
                  </div>
                  <p className="mt-2 leading-6">{demoMutation.error.message}</p>
                  <p className="mt-2 text-red-100/75">
                    Si el backend respondió 402, crea checkout. Si respondió 403, la demo pública está desactivada y debes usar un workspace autenticado.
                  </p>
                </div>
              ) : demoMutation.data ? (
                <pre className="max-h-[460px] overflow-auto rounded-2xl border border-white/10 bg-black/30 p-5 text-sm leading-6 text-slate-100">{JSON.stringify(demoMutation.data, null, 2)}</pre>
              ) : (
                <div className="rounded-2xl border border-white/10 bg-black/20 p-6 text-sm leading-6 text-slate-400">
                  Ejecuta una demo para ver el run del producto, el workspace creado, los controles de pago/fraude y las rutas de workers reales.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-[1.5rem] border-white/10 bg-white/[0.045]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-amber-200" />
                Guardrails
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2 md:grid-cols-2">
              {product.guardrails.map((guardrail) => (
                <div key={guardrail} className="rounded-2xl border border-white/10 bg-black/20 p-3 text-sm leading-6 text-slate-300">{guardrail}</div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PaymentPanel({
  kind,
  product,
  selectedUnit,
  accountEmail,
  onAccountEmailChange,
  account,
  accountError,
  accountNotice,
  activeSubscription,
  billingActive,
  activeWorkspaceId,
  loginError,
  isLoggingIn,
  onLogin,
  subscriptionData,
  subscriptionError,
  isSubscribing,
  onSubscribe,
  hasWebToken,
  suitePaymentToken,
  suiteWorkspaceId,
  checkoutData,
  checkoutError,
  isCheckingOut,
  onCheckout,
}: {
  kind: ProductKind;
  product: Product;
  selectedUnit?: ProductUnit;
  accountEmail: string;
  onAccountEmailChange: (value: string) => void;
  account?: AccountContext;
  accountError?: string;
  accountNotice?: string;
  activeSubscription?: AccountContext["subscriptions"][number];
  billingActive: boolean;
  activeWorkspaceId: string;
  loginError: Error | null;
  isLoggingIn: boolean;
  onLogin: () => void;
  subscriptionData?: SubscriptionCheckoutResult;
  subscriptionError: Error | null;
  isSubscribing: boolean;
  onSubscribe: () => void;
  hasWebToken: boolean;
  suitePaymentToken: string | null;
  suiteWorkspaceId: string | null;
  checkoutData?: CheckoutResult;
  checkoutError: Error | null;
  isCheckingOut: boolean;
  onCheckout: () => void;
}) {
  const paidTier = product.pricing.find((tier) => tier.priceUsd !== "0") || product.pricing[0];
  const price = paidTier ? formatTierPrice(paidTier.priceUsd) : "Precio no definido";
  const unitRequiresPayment = kind === "web" && selectedUnit?.requiresPayment !== false;
  const tokenLabel = kind === "web" ? "runToken" : "paymentToken/workspace";
  const tokenReady = kind === "web" ? hasWebToken || Boolean(checkoutData?.runToken) : Boolean((suitePaymentToken || checkoutData?.paymentToken) && (suiteWorkspaceId || checkoutData?.workspace?.id));
  const accountReady = Boolean(account?.user);
  const subscriptionReady = Boolean(activeSubscription && billingActive);

  return (
    <div className="space-y-4 rounded-2xl border border-amber-200/20 bg-amber-300/10 p-4 text-sm leading-6 text-amber-50">
      <div className="flex items-center gap-2 font-black">
        <CreditCard className="h-4 w-4" />
        Cuenta, suscripción y checkout
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
        <p className="text-xs uppercase tracking-[0.2em] text-amber-100/70">1. Cuenta</p>
        <div className="mt-3 grid gap-2">
          <InfoRow label="Sesión" value={accountReady ? account?.user?.email || "Activa" : "Pendiente"} />
          {accountError ? <p className="text-xs text-red-100">{accountError}</p> : null}
          {accountNotice ? <p className="text-xs text-emerald-100">{accountNotice}</p> : null}
          {!accountReady ? (
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <Input
                value={accountEmail}
                onChange={(event) => onAccountEmailChange(event.target.value)}
                placeholder="tu@email.com"
                className="border-white/10 bg-slate-950/70"
              />
              <Button
                type="button"
                variant="outline"
                onClick={onLogin}
                disabled={isLoggingIn}
                className="rounded-2xl border-amber-200/30 bg-amber-200/10 text-amber-50 hover:bg-amber-200/20"
              >
                {isLoggingIn ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Entrar
              </Button>
            </div>
          ) : null}
          {loginError ? <p className="text-xs text-red-100">{loginError.message}</p> : null}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
        <p className="text-xs uppercase tracking-[0.2em] text-amber-100/70">2. Suscripción recurrente</p>
        <div className="mt-3 grid gap-2">
          <InfoRow label="Plan recomendado" value={`${paidTier?.name || "Plan"} · ${price}`} />
          <InfoRow label="Estado" value={subscriptionReady ? `${activeSubscription?.status} · ${activeSubscription?.plan}` : "Sin suscripción activa"} />
          <InfoRow label="Workspace" value={activeWorkspaceId || "Se crea al suscribirte"} />
        </div>
        <Button
          type="button"
          onClick={onSubscribe}
          disabled={isSubscribing}
          className="mt-3 h-11 w-full rounded-2xl bg-emerald-200 text-slate-950 hover:bg-white"
        >
          {isSubscribing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
          {subscriptionReady ? "Actualizar estado de suscripción" : "Suscribirme y crear workspace"}
        </Button>
        {subscriptionData && (
          <div className="mt-3 rounded-xl border border-emerald-200/20 bg-emerald-400/10 p-3 text-emerald-50">
            Suscripción: {subscriptionData.subscription?.status || subscriptionData.checkoutStatus || "creada"}{subscriptionData.provider ? ` via ${subscriptionData.provider}` : ""}.
            {subscriptionData.workspace?.id ? ` Workspace: ${subscriptionData.workspace.id}.` : ""}
          </div>
        )}
        {subscriptionError && (
          <div className="mt-3 rounded-xl border border-red-300/20 bg-red-500/10 p-3 text-red-100">{subscriptionError.message}</div>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
        <p className="text-xs uppercase tracking-[0.2em] text-amber-100/70">3. Pago puntual alternativo</p>
      <div className="mt-3 grid gap-2">
        <InfoRow label="Precio activo" value={price} />
        <InfoRow label="Módulo/tool" value={selectedUnit?.name || "Selecciona herramienta"} />
        <InfoRow label="Requisito" value={unitRequiresPayment ? "Checkout antes de ejecutar" : kind === "web" ? "Módulo gratis" : "Demo pública o workspace pago"} />
        <InfoRow label={tokenLabel} value={tokenReady ? "Disponible" : "Pendiente"} />
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={onCheckout}
        disabled={isCheckingOut}
        className="mt-4 h-11 w-full rounded-2xl border-amber-200/30 bg-amber-200/10 text-amber-50 hover:bg-amber-200/20"
      >
        {isCheckingOut ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
        {kind === "web" ? "Crear checkout/runToken" : "Crear checkout/workspace"}
      </Button>
      {checkoutData && (
        <div className="mt-3 rounded-xl border border-emerald-200/20 bg-emerald-400/10 p-3 text-emerald-50">
          Checkout: {checkoutData.checkoutStatus || "creado"}{checkoutData.provider ? ` via ${checkoutData.provider}` : ""}.
          {checkoutData.workspace?.id ? ` Workspace: ${checkoutData.workspace.id}.` : ""}
        </div>
      )}
      {checkoutError && (
        <div className="mt-3 rounded-xl border border-red-300/20 bg-red-500/10 p-3 text-red-100">{checkoutError.message}</div>
      )}
      </div>
    </div>
  );
}

function DisclaimerNotice({ disclaimers, selectedUnit }: { disclaimers: string[]; selectedUnit?: ProductUnit }) {
  const items = Array.from(new Set([selectedUnit?.disclaimer, ...disclaimers].filter((item): item is string => Boolean(item))));
  if (!items.length) return null;

  return (
    <section className="rounded-[1.5rem] border border-amber-200/25 bg-amber-500/10 p-5 text-amber-50">
      <div className="flex items-center gap-2 text-sm font-black">
        <AlertTriangle className="h-4 w-4" />
        Disclaimer de uso responsable
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {items.slice(0, 4).map((item) => (
          <p key={item} className="rounded-2xl border border-amber-200/15 bg-black/20 p-3 text-sm leading-6 text-amber-50/90">
            {item}
          </p>
        ))}
      </div>
    </section>
  );
}

function Badge({ children }: { children: string | number }) {
  return <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300">{children}</span>;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="font-black text-white">{value}</span>
    </div>
  );
}

function formatTierPrice(priceUsd: string) {
  if (priceUsd === "0") return "Free";
  return `USD ${priceUsd}`;
}

function RiskBadge({ risk }: { risk: ProductUnit["risk"] }) {
  const klass = risk === "high" ? "border-rose-200/25 bg-rose-400/10 text-rose-100" : risk === "medium" ? "border-amber-200/25 bg-amber-400/10 text-amber-100" : "border-emerald-200/25 bg-emerald-400/10 text-emerald-100";
  return <span className={`rounded-full border px-2 py-1 text-[0.65rem] uppercase ${klass}`}>{risk}</span>;
}

function defaultWorkflow(kind: ProductKind) {
  if (kind === "messaging") {
    return ["Conecta WhatsApp Cloud API o Telegram.", "Recibe mensajes, audios o documentos.", "El producto enruta a una herramienta.", "El usuario aprueba acciones sensibles.", "Se guarda historial, auditoría y próximos pasos."];
  }
  return ["Usuario elige módulo.", "Backend valida workspace, pago/idempotencia y fraude.", "Producto enruta a workers internos.", "Se generan resultados/artifacts.", "Se registra run y auditoría."];
}

type ProductField = { name: string; label: string; placeholder: string };

function fieldsForUnit(kind: ProductKind, unitId: string, productId: string): ProductField[] {
  const id = `${productId} ${unitId}`.toLowerCase();
  if (kind === "messaging" && /catalog|inventory|menu|loyalty|store|tiendita/.test(id)) {
    return [
      { name: "name", label: "Producto o item", placeholder: "Camiseta negra M" },
      { name: "price", label: "Precio", placeholder: "45000" },
      { name: "stock", label: "Stock", placeholder: "10" },
      { name: "customer", label: "Cliente", placeholder: "Maria Perez" },
    ];
  }
  if (/invoice|receipt|quote|proposal|contract/.test(id)) {
    return [
      { name: "customer", label: "Cliente", placeholder: "Cliente o empresa" },
      { name: "amount", label: "Monto", placeholder: "180000" },
      { name: "items", label: "Items", placeholder: "3 productos, servicio mensual..." },
      { name: "deadline", label: "Fecha/plazo", placeholder: "2026-05-15" },
    ];
  }
  if (/calendar|meeting|task|reminder|maintenance|subscription|agenda/.test(id)) {
    return [
      { name: "title", label: "Titulo", placeholder: "Reunion con Juan" },
      { name: "dueAt", label: "Fecha/hora", placeholder: "2026-05-01 15:00" },
      { name: "contact", label: "Contacto", placeholder: "Juan" },
      { name: "recurrence", label: "Recurrencia", placeholder: "daily, weekly, monthly" },
    ];
  }
  if (/legal|petition|claim|ticket|rights|immigration|dispute/.test(id)) {
    return [
      { name: "country", label: "Pais/jurisdiccion", placeholder: "Colombia" },
      { name: "entity", label: "Entidad/empresa", placeholder: "EPS, SIC, Transito..." },
      { name: "deadline", label: "Plazo", placeholder: "10 dias habiles" },
      { name: "evidence", label: "Evidencia", placeholder: "Factura, multa, radicado..." },
    ];
  }
  return [];
}

function buildOperationalPayload(kind: ProductKind, productId: string, unitId: string, text: string, manual: Record<string, string>): Record<string, string> {
  const parsed = parseTextHints(text);
  const merged = { ...parsed, ...compact(manual) };
  return {
    channel: kind === "messaging" ? "whatsapp" : "web",
    productId,
    unitId,
    ...merged,
  };
}

function parseTextHints(text: string): Record<string, string> {
  const normalized = text.replace(/\s+/g, " ").trim();
  const priceMatch = normalized.match(/(?:\$|cop|usd)?\s?(\d[\d.,]{2,})(?:\s?(?:cop|usd))?/i);
  const stockMatch = normalized.match(/(?:stock|inventario|quedan|cantidad)\s*[:=]?\s*(\d+)/i);
  const dateMatch = normalized.match(/(\d{4}-\d{2}-\d{2}(?:[ t]\d{1,2}:?\d{0,2})?|\b(?:hoy|mañana|manana|lunes|martes|miercoles|miércoles|jueves|viernes|sabado|sábado|domingo)\b[^,.]*)/i);
  const nameMatch = normalized.match(/(?:agregar|producto|item|vender|vendí|vendi)\s*[:=]?\s*([^,.]+?)(?:\s+(?:por|a|stock|cantidad|quedan|\$|\d{4,}))/i);
  return compact({
    price: priceMatch?.[1]?.replace(/[.,](?=\d{3}\b)/g, ""),
    amount: priceMatch?.[1]?.replace(/[.,](?=\d{3}\b)/g, ""),
    stock: stockMatch?.[1],
    dueAt: dateMatch?.[1],
    name: nameMatch?.[1]?.trim(),
    items: normalized || undefined,
  });
}

function compact(input: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(Object.entries(input).filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== ""));
}
